/*
 * Project: Goalscape
 * File:    projectHandlers.js
 * Path:    src/utils/projectHandlers.js
 * Description: Utility functions for handling project operations including todos, columns, and cards.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement optimistic updates for UI responsiveness and consider using server-side functions for complex operations.
 */
import { doc, updateDoc, getDoc } from 'firebase/firestore'; // <--- Add getDoc
import { db } from '../firebase'; // <--- Make sure db is imported
import { addActivity } from './cardUtils';

/*
 * Function: updateProject
 * Description: Updates a project's data in Firebase.
 * Parameters:
 *   projectId: string - The ID of the project to update.
 *   updates: object - The updates to apply to the project.
 * Returns: Promise<void>
 * Usage:
 *   await updateProject('project123', { title: 'New Title' });
 * AI Hint: Use transactions to ensure atomicity when updating multiple project properties.
 */
export const updateProject = async (projectId, updates) => {
    try {
        const projectRef = doc(db, 'projects', projectId);
        await updateDoc(projectRef, {
            ...updates,
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error updating project:', error);
        throw error;
    }
};

/*
 * Object: handleTodoOperations
 * Description: Collection of functions for managing project todos.
 * Methods:
 *   add: Adds a new todo.
 *   toggle: Toggles todo completion status.
 *   delete: Removes a todo.
 *   update: Updates todo text.
 *   reorder: Reorders todos.
 *   convertToCard: Converts a todo to a card.
 *   convertAllToCards: Converts all incomplete todos to cards.
 * AI Hint: Consider using a subcollection for todos to improve scalability and query performance.
 */
export const handleTodoOperations = {
    add: async (projectId, project, text) => {
        const newTodo = {
            id: `todo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            text,
            completed: false
        };

        await updateProject(projectId, {
            todos: [...(project.todos || []), newTodo]
        });
    },

    toggle: async (projectId, project, index) => {
        const newTodos = [...(project.todos || [])];
        newTodos[index].completed = !newTodos[index].completed;
        await updateProject(projectId, { todos: newTodos });
    },

    delete: async (projectId, project, index) => {
        const newTodos = [...project.todos];
        newTodos.splice(index, 1);
        await updateProject(projectId, { todos: newTodos });
    },

    update: async (projectId, project, index, newText) => {
        const newTodos = [...project.todos];
        newTodos[index] = { ...newTodos[index], text: newText };
        await updateProject(projectId, { todos: newTodos });
    },

    reorder: async (projectId, project, result) => {
        if (!result.destination) return;

        const newTodos = Array.from(project.todos);
        const [removed] = newTodos.splice(result.source.index, 1);
        newTodos.splice(result.destination.index, 0, removed);
        await updateProject(projectId, { todos: newTodos });
    },

    convertToCard: async (projectId, project, todo, user) => {
        const firstColumnId = project.columnOrder[0];
        if (!firstColumnId) return;

        const creationActivity = addActivity(user, 'created the card');
        const newCard = {
            id: `card-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            title: todo.text,
            description: '',
            comments: [],
            activity: [creationActivity],
            createdFrom: 'todo',
            dueDate: null,
            isDueDateCompleted: false
        };

        const updatedColumns = {
            ...project.columns,
            [firstColumnId]: {
                ...project.columns[firstColumnId],
                cards: [...(project.columns[firstColumnId].cards || []), newCard]
            }
        };

        const updatedTodos = project.todos.filter(t => t.id !== todo.id);

        await updateProject(projectId, {
            columns: updatedColumns,
            todos: updatedTodos
        });
    },

    convertAllToCards: async (projectId, project, incompleteTodos, user) => {
        const firstColumnId = project.columnOrder[0];
        if (!firstColumnId) return;

        const newCards = incompleteTodos.map(todo => {
            const creationActivity = addActivity(user, 'created the card');
            return {
                id: `card-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${todo.id}`,
                title: todo.text,
                description: '',
                comments: [],
                activity: [creationActivity],
                createdFrom: 'todo',
                dueDate: null,
                isDueDateCompleted: false
            };
        });

        const updatedColumns = {
            ...project.columns,
            [firstColumnId]: {
                ...project.columns[firstColumnId],
                cards: [
                    ...(project.columns[firstColumnId].cards || []),
                    ...newCards
                ]
            }
        };

        const updatedTodos = project.todos.filter(todo => todo.completed);

        await updateProject(projectId, {
            columns: updatedColumns,
            todos: updatedTodos
        });
    }
};

/*
 * Object: handleColumnOperations
 * Description: Collection of functions for managing project columns.
 * Methods:
 *   add: Adds a new column.
 *   update: Updates column properties.
 *   delete: Removes a column.
 *   reorder: Reorders columns.
 * AI Hint: Implement column-specific permissions to restrict access based on user roles.
 */
export const handleColumnOperations = {
    add: async (projectId) => { // <--- Remove 'project' from arguments
        const projectRef = doc(db, 'projects', projectId);
        try {
            const projectSnap = await getDoc(projectRef); // <--- Fetch the project document
            if (!projectSnap.exists()) {
                console.error("Project not found!");
                return;
            }
            const currentProjectData = projectSnap.data(); // <--- Get current project data

            const timestamp = Date.now();
            const newColumnId = `column-${timestamp}-${Math.random().toString(36).substr(2, 9)}`;
            const newColumn = {
                id: newColumnId,
                title: 'New Column',
                cards: []
            };

            await updateProject(projectId, {
                columns: {
                    ...currentProjectData.columns, // <--- Use columns from fetched data
                    [newColumnId]: newColumn
                },
                columnOrder: [...currentProjectData.columnOrder, newColumnId]
            });
        } catch (error) {
            console.error("Error adding column:", error);
            // Potentially re-throw the error or handle it as needed
            throw error;
        }
    },

    update: async (projectId, project, columnId, updates) => {
        await updateProject(projectId, {
            columns: {
                ...project.columns,
                [columnId]: {
                    ...project.columns[columnId],
                    ...updates
                }
            }
        });
    },

    delete: async (projectId, project, columnId) => {
        const { [columnId]: deletedColumn, ...remainingColumns } = project.columns;
        const updatedColumnOrder = project.columnOrder.filter(id => id !== columnId);

        await updateProject(projectId, {
            columns: remainingColumns,
            columnOrder: updatedColumnOrder
        });
    },

    reorder: async (projectId, project, newColumnOrder) => {
        await updateProject(projectId, { columnOrder: newColumnOrder });
    }
};

/*
 * Object: handleCardOperations
 * Description: Collection of functions for managing cards within columns.
 * Methods:
 *   add: Adds a new card to a column.
 *   move: Moves a card between columns or within the same column.
 * AI Hint: Implement card archiving to preserve card history without cluttering the board.
 */
export const handleCardOperations = {
    add: async (projectId, project, columnId, cardTitle, user) => {
        const creationActivity = addActivity(user, 'created the card');
        const newCard = {
            id: `card-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            title: cardTitle && cardTitle.trim() !== '' ? cardTitle.trim() : 'New Card',
            description: '',
            comments: [],
            activity: [creationActivity],
            dueDate: null,
            isDueDateCompleted: false
        };

        await updateProject(projectId, {
            columns: {
                ...project.columns,
                [columnId]: {
                    ...project.columns[columnId],
                    cards: [...(project.columns[columnId].cards || []), newCard]
                }
            }
        });
    },

    move: async (projectId, project, sourceColumn, destColumn, movedCard, newSourceCards, newDestCards) => {
        try {
            const updates = {
                columns: {
                    ...project.columns
                }
            };

            updates.columns[sourceColumn.id] = {
                ...sourceColumn,
                cards: newSourceCards
            };

            if (sourceColumn.id !== destColumn.id) {
                updates.columns[destColumn.id] = {
                    ...destColumn,
                    cards: newDestCards
                };
            }

            await updateProject(projectId, updates);
        } catch (error) {
            console.error('Error moving card:', error);
            throw error;
        }
    }
};
